<!-- TODO: refactor to MapLayer and MapSource vue components -->
<script setup>
import {computed, onBeforeUnmount, onMounted, ref, watchEffect, watch} from 'vue'
import {useStore} from 'vuex'
import {useRoute, useRouter} from 'vue-router'
import { v4 as uuidv4 } from 'uuid'
import {bbox} from '@turf/turf'

import {Trash2, Eye, Upload, Edit2, FolderOpen} from 'lucide-vue-next'
import {useToast} from '@/components/ui/toast/use-toast'
import {Button} from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ProjectSelect } from '@/components/ui/project-select'
import { BulkUploadTable, BulkEditDialog, UploadProgressDialog } from '@/components/bulk-upload'
import { HOST, API } from '@/utils/http'

import {parseKML, parseKMZ, parseGeoJSON, parseImage, parseGPX, parseFIT} from '@/composables/files'
import { uploadResource } from '@/services/resourcesApi'

const { toast } = useToast()
const route = useRoute()
const router = useRouter()
const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const loadedFiles = computed(() => store.getters['geo/getFiles'])
const orgProjects = ref([])
const projects = computed(() => orgProjects.value)
const currentProject = computed(() => store.getters['project/getProject'])
const selectedOrgId = computed(() => route.params.org_id)

const MAX_FILES = 30

const isUploading = ref(false)
const selectedFeatures = ref([])
const showBulkEditDialog = ref(false)
const showUploadProgressDialog = ref(false)
const uploadProgressRef = ref(null)
const selectedProjectId = ref(null)
const uploadCancelled = ref(false)

const collection = ref({
  type: 'FeatureCollection',
  features: []
})

// Per-file visibility (default true)
const fileVisibility = ref({})
// Per-file MultiPolygon grouping toggle (default true = keep grouped)
const fileGroupPolygons = ref({})

// Effective features: when groupPolygons is OFF for a file, split MultiPolygons into individual Polygons
const effectiveFeatures = computed(() => {
  const result = []
  for (const feature of collection.value.features) {
    const fileName = feature.properties?.fileName
    const shouldGroup = fileGroupPolygons.value[fileName] !== false

    if (!shouldGroup && feature.geometry?.type === 'MultiPolygon') {
      // Split MultiPolygon into individual Polygons
      for (let i = 0; i < feature.geometry.coordinates.length; i++) {
        result.push({
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: feature.geometry.coordinates[i]
          },
          properties: {
            ...feature.properties,
            name: `${feature.properties?.name || 'Unnamed'} (${i + 1})`,
            local_uuid: `${feature.properties.local_uuid}_split_${i}`,
            _parentUuid: feature.properties.local_uuid,
            _splitIndex: i,
            isEditing: false
          }
        })
      }
    } else {
      result.push(feature)
    }
  }
  return result
})

// Group effective features by fileName
const fileGroups = computed(() => {
  const groups = {}
  for (const feature of effectiveFeatures.value) {
    const fileName = feature.properties?.fileName || 'Unknown'
    if (!groups[fileName]) {
      groups[fileName] = []
    }
    groups[fileName].push(feature)
  }

  return Object.entries(groups).map(([fileName, features]) => ({
    fileName,
    features,
    visible: fileVisibility.value[fileName] !== false,
    groupPolygons: fileGroupPolygons.value[fileName] !== false
  }))
})

// Features to show on the map: filter by file visibility and editing state
const visibleMapFeatures = computed(() => {
  return effectiveFeatures.value.filter(f => {
    const fileName = f.properties?.fileName
    if (fileVisibility.value[fileName] === false) return false
    if (f.properties?.isEditing) return false
    return true
  })
})

const syncMapSource = () => {
  if (!map.value?.getSource('temp_data')) return
  map.value.getSource('temp_data').setData({
    type: 'FeatureCollection',
    features: visibleMapFeatures.value
  })
}

// Auto-sync map when visible features change
watch(visibleMapFeatures, () => {
  syncMapSource()
}, { deep: true })

const toggleFileVisibility = (fileName) => {
  fileVisibility.value[fileName] = !(fileVisibility.value[fileName] !== false)
}

const toggleFileGroupPolygons = (fileName) => {
  fileGroupPolygons.value[fileName] = !(fileGroupPolygons.value[fileName] !== false)
}

const activeLayers = []
const activeSources = []

const setCollection = (fileCollection, fileName) => {
  createLayers()

  // Initialize per-file state for new files
  if (!(fileName in fileVisibility.value)) {
    fileVisibility.value[fileName] = true
  }
  if (!(fileName in fileGroupPolygons.value)) {
    fileGroupPolygons.value[fileName] = true
  }

  for (let feature of fileCollection.features) {
    // Assign map center as default geometry for images without GPS data
    if (!feature.geometry && feature.properties?.needsLocation && map.value) {
      const center = map.value.getCenter()
      feature.geometry = { type: 'Point', coordinates: [center.lng, center.lat] }
    }

    // Preserve existing properties from the parsed file
    feature.properties = {
      ...feature.properties, // Keep all existing properties including description, name, etc.
      fileName: fileName,
      // Only set name as fallback if it doesn't exist
      name: feature.properties.name || feature.properties.Name || fileName.split('.').shift(),
      description: '',
      local_uuid: uuidv4(),
      isEditing: false
    }
    collection.value.features.push(feature)
  }

  // Auto-combine Polygons into MultiPolygon when file has >5 Polygons
  const polygonsFromFile = collection.value.features.filter(
    f => f.properties?.fileName === fileName && f.geometry?.type === 'Polygon'
  )
  if (polygonsFromFile.length > 5) {
    const multiPolygon = {
      type: 'Feature',
      geometry: {
        type: 'MultiPolygon',
        coordinates: polygonsFromFile.map(f => f.geometry.coordinates)
      },
      properties: {
        ...polygonsFromFile[0].properties,
        name: polygonsFromFile[0].properties?.name || fileName.split('.').shift(),
        local_uuid: uuidv4(),
        _autoCombined: true,
        isEditing: false
      }
    }

    // Remove individual Polygons and add the combined MultiPolygon
    const polygonUuids = new Set(polygonsFromFile.map(f => f.properties.local_uuid))
    collection.value.features = collection.value.features.filter(
      f => !polygonUuids.has(f.properties?.local_uuid)
    )
    collection.value.features.push(multiPolygon)
  }

  syncMapSource()
}

const splitFeature = (feature) => {
  collection.value.features = collection.value.features.filter(f => f.properties.local_uuid !== feature.properties.local_uuid)

  if (feature.geometry?.type === 'GeometryCollection') {
    for (let geometry of feature.geometry.geometries) {
      const geoFeature = {
        type: 'Feature',
        geometry: geometry,
        properties: {
          ...feature.properties ? feature.properties : {},
          // Preserve the original name and description
          name: feature.properties.name || feature.properties.fileName?.split('.').shift() || 'Unnamed',
          description: feature.properties.description || '',
          fileName: feature.properties.fileName,
          local_uuid: uuidv4(),
          isEditing: false
        }
      }
      collection.value.features.push(geoFeature)
    }
  }
}

const handleFile = async (file) => {
  const ext = file.name.split('.').pop().toLowerCase()
  let collection, name

  switch (ext) {
    case 'kml':
      ({ collection, name } = await parseKML(file))
      break
    case 'kmz':
      ({ collection, name } = await parseKMZ(file))
      break
    case 'geojson':
      ({ collection, name } = await parseGeoJSON(file))
      break
    case 'gpx':
      ({ collection, name } = await parseGPX(file))
      break
    case 'fit':
      ({ collection, name } = await parseFIT(file))
      break
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'heic':
    case 'heif':
    case 'avif':
    case 'webp':
      ({ collection, name } = await parseImage(file))
      break
    default:
      toast({ title: 'Invalid file type', status: 'error' })
      break
  }

  if (collection && name) {
    setCollection(collection, name)
  }
}

const handleFiles = async (files) => {
  if (!files) return
  if (files.length === 0) return

  if (files.length > MAX_FILES) {
    toast({title: `Only ${MAX_FILES} files can be uploaded at once`})
    return
  }

  for (let i = 0; i < files.length; i++) {
    if (i > MAX_FILES) break
    await handleFile(files[i])
  }
}

const createLayers = () => {
  if (!map.value.getSource('temp_data')) {
    map.value.addSource('temp_data', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      },
    });
    activeLayers.push('temp_data');
  }

  if (!map.value.getLayer('temp-geometry-layer-fill')) {
    map.value.addLayer({
      'id': 'temp-geometry-layer-fill',
      'type': 'line',
      'source': 'temp_data',
      'paint': {
        'line-color': ['case', ['has', 'color'], ['get', 'color'], '#0059ff'],
        'line-width': 4
      },
      'filter': ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']]
    });
    activeLayers.push('temp-geometry-layer-fill');
  }

  if (!map.value.getLayer('temp-geometry-layer-lines')) {
    map.value.addLayer({
      'id': 'temp-geometry-layer-lines',
      'type': 'fill',
      'source': 'temp_data',
      'paint': {
        'fill-color': ['case', ['has', 'color'], ['get', 'color'], '#0059ff'],
        'fill-opacity': 0.03
      },
      'filter': ['==', '$type', 'Polygon']
    });
    activeLayers.push('temp-geometry-layer-lines');
  }

  if (!map.value.getLayer('temp-geometry-layer-points')) {
    map.value.addLayer({
      id: 'temp-geometry-layer-points',
      type: 'circle',
      source: 'temp_data',
      paint: {
        'circle-color': ['case', ['has', 'color'], ['get', 'color'], '#0059ff'],
        'circle-radius': 8,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff'
      },
      'filter': ['==', '$type', 'Point']
    });
    activeLayers.push('temp-geometry-layer-points');
  }

  if (!map.value.getSource('temp_data')) return
  syncMapSource()
  activeSources.push('temp_data')
}

const focusCollection = () => {
  const bounds = bbox(collection.value)
  map.value.fitBounds(bounds, { padding: 20 })
}

const removeFeature = async (feature) => {
  // If it's a split feature, remove just the selected polygon from the parent MultiPolygon
  if (feature.properties?._parentUuid) {
    const parentUuid = feature.properties._parentUuid
    const splitIndex = feature.properties._splitIndex
    const parentFeature = collection.value.features.find(
      f => f.properties?.local_uuid === parentUuid
    )
    if (
      parentFeature &&
      typeof splitIndex === 'number' &&
      parentFeature.geometry?.type === 'MultiPolygon' &&
      parentFeature.geometry.coordinates[splitIndex]
    ) {
      parentFeature.geometry.coordinates.splice(splitIndex, 1)
      if (parentFeature.geometry.coordinates.length === 0) {
        collection.value.features = collection.value.features.filter(f => f !== parentFeature)
      }
    }
  } else {
    collection.value.features = collection.value.features.filter(f => f !== feature)
  }
  syncMapSource()

  if (collection.value.features.length === 0) {
    if (feature.properties?.code) {
      await createFeature(feature)
    }
  }
}

const clearCollection = () => {
  collection.value.features = []
  selectedFeatures.value = []
  fileVisibility.value = {}
  fileGroupPolygons.value = {}
  syncMapSource()
}

const startEditing = (feature) => {
  for (let f of collection.value.features) {
    if (f.properties.local_uuid === feature.properties.local_uuid) {
      f.properties.isEditing = !f.properties.isEditing
    } else {
      f.properties.isEditing = false
    }
  }
  syncMapSource()
}

const stopEditing = (feature) => {
  for (let f of collection.value.features) {
    if (f.properties.local_uuid === feature.properties.local_uuid) {
      f.geometry = feature.geometry
      f.properties.isEditing = false
    }
  }
  syncMapSource()
}

const updateFeature = (feature) => {
  for (let f of collection.value.features) {
    if (f.properties.local_uuid === feature.properties.local_uuid) {
      f.properties = feature.properties
    }
  }
  syncMapSource()
}

const reset = () => {
  for (let layer of activeLayers) {
    if (map.value.getLayer(layer)) map.value.removeLayer(layer)
  }

  for (let source of activeSources) {
    if (map.value.getSource(source)) map.value.removeSource(source)
  }

  collection.value.features = []
  selectedFeatures.value = []
  fileVisibility.value = {}
  fileGroupPolygons.value = {}
  store.commit('geo/SET_FILES', null)
}

// New bulk upload methods
const handleSelectFeatures = (features) => {
  selectedFeatures.value = features
}

const handlePreviewFeature = (feature) => {
  const bounds = bbox(feature)
  map.value.fitBounds(bounds, { padding: 20 })
}

const zoomToFile = (fileName) => {
  const group = fileGroups.value.find(g => g.fileName === fileName)
  if (!group || group.features.length === 0) return
  const fc = { type: 'FeatureCollection', features: group.features }
  const bounds = bbox(fc)
  map.value.fitBounds(bounds, { padding: 50 })
}

const handleEditFeature = (feature) => {
  // For now, just focus on the feature
  handlePreviewFeature(feature)
  // TODO: Implement individual feature editing if needed
}

const handleDeleteFeature = (feature) => {
  removeFeature(feature)
}

const handleFeatureUploaded = (originalFeature, uploadedFeature) => {
  // Remove the feature from the collection after successful upload
  const index = collection.value.features.findIndex(f =>
    f.properties?.local_uuid === originalFeature.properties?.local_uuid
  )
  if (index !== -1) {
    collection.value.features.splice(index, 1)
  }

  syncMapSource()

  toast({
    title: 'Feature uploaded successfully',
    description: `${uploadedFeature.properties.name} has been uploaded to the project`
  })
}

const handleBulkEdit = () => {
  showBulkEditDialog.value = true
}

const handleBulkEditApply = (changes) => {
  // Apply changes to selected features
  selectedFeatures.value.forEach(feature => {
    // Apply name changes
    if (changes.nameOperation) {
      const currentName = feature.properties.name || feature.properties.fileName || 'Unnamed'

      switch (changes.nameOperation) {
        case 'replace':
          feature.properties.name = changes.nameValue
          break
        case 'prefix':
          feature.properties.name = `${changes.nameValue}${currentName}`
          break
        case 'suffix':
          feature.properties.name = `${currentName}${changes.nameValue}`
          break
      }
    }

    // Apply description changes
    if (changes.descriptionOperation) {
      const currentDescription = feature.properties.description || ''

      switch (changes.descriptionOperation) {
        case 'replace':
          feature.properties.description = changes.descriptionValue
          break
        case 'append':
          feature.properties.description = currentDescription + changes.descriptionValue
          break
      }
    }

    // Apply other changes
    if (changes.colorValue) {
      feature.properties.color = changes.colorValue
    }

    if (changes.visibilityValue !== undefined) {
      feature.properties.shared = changes.visibilityValue
    }

    if (changes.projectValue) {
      feature.properties.project_id = changes.projectValue
    }

    if (changes.staticValue !== undefined) {
      feature.properties.is_static = changes.staticValue
    }
  })

  syncMapSource()

  toast({
    title: `Updated ${selectedFeatures.value.length} features`,
  })
}

const handleUploadSelected = async (features = undefined) => {
  // If no features passed, use all features (backward compatibility)
  let featuresToProcess = features
  if (!featuresToProcess) {
    featuresToProcess = collection.value.features
  }

  if (featuresToProcess.length === 0) {
    toast({ title: 'No features to upload' })
    return
  }

  // Check if a project is selected
  const projectId = selectedProjectId.value || currentProject.value?.project_id
  if (!projectId) {
    toast({ title: 'Please select a project first', variant: 'destructive' })
    return
  }

  showUploadProgressDialog.value = true
  isUploading.value = true
  uploadCancelled.value = false

  // Process uploads one by one
  for (let i = 0; i < featuresToProcess.length; i++) {
    // Check if upload was cancelled
    if (uploadCancelled.value) {
      break
    }

    const feature = featuresToProcess[i]

    try {
      // Update progress
      uploadProgressRef.value?.updateFeatureStatus(feature.properties.local_uuid, 'uploading', null, 0)

      // Prepare feature data for upload
      const featureData = {
        type: 'Feature',
        properties: {
          name: feature.properties.name || feature.properties.fileName || 'Unnamed',
          description: feature.properties.description || '',
          shared: feature.properties.shared || false,
          is_static: feature.properties.is_static || false,
          member_id: store.getters['user/getMember']?.member_id,
          created_origin: Date.now(),
          type: feature.geometry.type,
          subtype: 'report',
          color: feature.properties.color || '#0059ff',
          project_id: feature.properties.project_id || projectId,
          data: []
        },
        geometry: feature.geometry
      }

      // Create the feature
      const result = await store.dispatch('geo/saveFeature', {
        project_id: feature.properties.project_id || projectId,
        files: [], // No files for bulk upload
        feature: featureData
      })

      // Upload resource file (e.g., image) if present
      let resourceFailed = false
      if (feature.properties.resource && result?.properties?.code) {
        try {
          await uploadResource(feature.properties.resource, result.properties.code)
        } catch (uploadError) {
          console.error('Failed to upload resource:', uploadError)
          resourceFailed = true
        }
      }

      if (resourceFailed) {
        uploadProgressRef.value?.updateFeatureStatus(feature.properties.local_uuid, 'error', 'Feature created but resource upload failed')
      } else {
        uploadProgressRef.value?.updateFeatureStatus(feature.properties.local_uuid, 'success')
      }

      // Remove from collection — handle split features
      if (feature.properties?._parentUuid) {
        // Check if all split siblings have been uploaded
        const parentUuid = feature.properties._parentUuid
        const remainingSiblings = featuresToProcess.filter(
          f => f.properties?._parentUuid === parentUuid &&
          f.properties.local_uuid !== feature.properties.local_uuid
        )
        const allSiblingsProcessed = remainingSiblings.every(sib => {
          const sibIdx = featuresToProcess.indexOf(sib)
          return sibIdx < i // already processed
        })
        if (allSiblingsProcessed) {
          collection.value.features = collection.value.features.filter(
            f => f.properties.local_uuid !== parentUuid
          )
        }
      } else {
        collection.value.features = collection.value.features.filter(
          f => f.properties.local_uuid !== feature.properties.local_uuid
        )
      }

    } catch (error) {
      uploadProgressRef.value?.updateFeatureStatus(feature.properties.local_uuid, 'error', error.message || 'Upload failed')
    }
  }

  syncMapSource()

  isUploading.value = false
  selectedFeatures.value = []

  // Only show completion toast if not cancelled
  if (!uploadCancelled.value) {
    toast({
      title: `Upload completed`,
    })
  }
}

const handleUploadCancel = () => {
  uploadCancelled.value = true
  isUploading.value = false
  showUploadProgressDialog.value = false

  toast({
    title: 'Upload stopped',
    description: 'The upload process has been cancelled'
  })
}

const handleUploadRetry = (failedFeatures) => {
  // Add failed features back to collection if they were removed
  failedFeatures.forEach(feature => {
    if (!collection.value.features.find(f => f.properties.local_uuid === feature.properties.local_uuid)) {
      collection.value.features.push(feature)
    }
  })

  syncMapSource()

  showUploadProgressDialog.value = false
}

const handleValidateFeature = (feature) => {
  syncMapSource()
}

const handleUpdateFeature = (feature) => {
  // Find and update the feature in the collection
  const index = collection.value.features.findIndex(f => f.properties.local_uuid === feature.properties.local_uuid)
  if (index !== -1) {
    collection.value.features[index] = feature
  }
  syncMapSource()
}

// Legacy method - now using handleUploadSelected instead

const createFeature = async (feature) => {
  try {
    const result = await store.dispatch('geo/createFeature', {
      geojson: feature,
      project_id: feature.properties.project_id
    })

    // router.push({name: 'ProjectHome', params: {project_id: feature.properties.project_id}})
    router.push({
      name: 'Feature',
      params: {
        org_id: feature.properties.organization_id,
        project_id: feature.properties.project_id,
        featureCode: feature.properties.code
      }
    })
  } catch (error) {
  }
}

const fetchOrgProjects = async () => {
  if (!selectedOrgId.value) return
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${selectedOrgId.value}/project`
    })
    if (response.data && Array.isArray(response.data)) {
      orgProjects.value = response.data
    }
  } catch (error) {
    console.error('Failed to fetch org projects:', error)
  }
}

// Initialize with current project if available
watchEffect(() => {
  if (currentProject.value && !selectedProjectId.value) {
    selectedProjectId.value = currentProject.value.project_id
  }
})

// Fetch all organization templates when component mounts
onMounted(async () => {
  if (selectedOrgId.value) {
    store.dispatch('geo/fetchAllTemplates', selectedOrgId.value)
    await fetchOrgProjects()
  }
})

// Fetch templates and projects when organization changes
watch(selectedOrgId, async (newOrgId) => {
  if (newOrgId) {
    store.dispatch('geo/fetchAllTemplates', newOrgId)
    await fetchOrgProjects()

    // Reset selected project if it's not in the new org's projects
    if (selectedProjectId.value && projects.value) {
      const projectExists = projects.value.some(p => p.project_id === selectedProjectId.value)
      if (!projectExists) {
        selectedProjectId.value = null
      }
    }
  }
})

watchEffect(async () => {
  await handleFiles(loadedFiles.value)
})

onBeforeUnmount(() => {
  reset()
})
</script>

<template>
  <div class="bg-background">
    <div v-if="collection.features.length > 0" class="p-4">
      <!-- Project Selector -->
      <div class="mb-6">
        <div class="flex items-center space-x-4">
          <div class="flex-1 max-w-sm">
            <ProjectSelect
              :projects="projects"
              :model-value="selectedProjectId?.toString() ?? ''"
              :placeholder="currentProject ? `Current: ${currentProject.project_name}` : 'Select a project'"
              @update:model-value="(val) => {
                selectedProjectId = val ? parseInt(val) : null
              }"
            />
          </div>
         </div>
       </div>

      <BulkUploadTable
        :file-groups="fileGroups"
        :is-uploading="isUploading"
        :selected-project="selectedProjectId ? projects.find(p => p.project_id === selectedProjectId) : currentProject"
        @select-features="handleSelectFeatures"
        @preview-feature="handlePreviewFeature"
        @edit-feature="handleEditFeature"
        @delete-feature="handleDeleteFeature"
        @bulk-edit="handleBulkEdit"
        @upload-selected="handleUploadSelected"
        @validate-feature="handleValidateFeature"
        @feature-uploaded="handleFeatureUploaded"
        @update-feature="handleUpdateFeature"
        @toggle-file-visibility="toggleFileVisibility"
        @toggle-file-group-polygons="toggleFileGroupPolygons"
        @zoom-to-file="zoomToFile"
      />
    </div>
    <div v-else class="text-center py-12">
      <Upload class="w-10 h-10 mx-auto mb-4" />
      <h4 class="mb-1 text-lg">
        Drag and drop files on the map
      </h4>
      <p class="text-xs text-muted-foreground mb-4">
        Supported formats: KML, KMZ, GeoJSON, GPX, FIT, and images with GPS data
      </p>
    </div>

    <!-- Bulk Edit Dialog -->
    <BulkEditDialog
      :open="showBulkEditDialog"
      :features="selectedFeatures"
      :projects="projects"
      @close="showBulkEditDialog = false"
      @apply="handleBulkEditApply"
    />

    <!-- Upload Progress Dialog -->
    <UploadProgressDialog
      ref="uploadProgressRef"
      :open="showUploadProgressDialog"
      :features="selectedFeatures.length > 0 ? selectedFeatures : collection.features"
      :is-uploading="isUploading"
      @close="showUploadProgressDialog = false"
      @cancel="handleUploadCancel"
      @retry="handleUploadRetry"
    />
  </div>
</template>
